import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownAZ, Clock } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const SORTS = [
  { key: "alpha", label: "A – Z", icon: ArrowDownAZ },
  { key: "latest", label: "Latest update", icon: Clock },
];

export default function ShowsPage() {
  const [shows, setShows] = useState([]);
  const [sort, setSort] = useState("alpha");
  useEffect(() => { api.get("/shows").then((r) => setShows(r.data)).catch(() => {}); }, []);

  const sorted = useMemo(() => {
    const list = [...shows];
    if (sort === "latest") list.sort((a, b) => (b.latest_episode_at || "").localeCompare(a.latest_episode_at || ""));
    else list.sort((a, b) => a.name.replace(/^the\s+/i, "").localeCompare(b.name.replace(/^the\s+/i, ""), undefined, { sensitivity: "base" }));
    return list;
  }, [shows, sort]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="shows-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-10 pb-32">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-2">The lineup</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none">All Shows</h1>
            <p className="mt-3 text-sm text-slate-400" data-testid="shows-count">{shows.length} shows</p>
          </div>
          <div className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1" role="group" aria-label="Sort shows" data-testid="shows-sort-toggle">
            {SORTS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setSort(key)} aria-pressed={sort === key} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${sort === key ? "bg-sky-400 text-[#070F1E]" : "text-slate-300 hover:text-white"}`} data-testid={`shows-sort-${key}`}>
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="shows-grid">
          {sorted.map((s, i) => (
            <Link key={s.id} to={`/shows/${s.slug}`} className="group card-surface card-surface-hover rounded-2xl p-3 reveal" style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }} data-testid={`show-card-${s.slug}`}>
              <div className="aspect-square rounded-xl overflow-hidden">
                {s.cover_url ? <img src={s.cover_url} alt={s.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="h-full w-full bg-[#053671]" />}
              </div>
              <p className="mt-3 text-[11px] font-mono uppercase tracking-widest text-sky-400 truncate">{s.subgroup || s.group || s.category}</p>
              <h3 className="font-display font-bold leading-snug">{s.name}</h3>
              <p className="text-xs text-slate-400 truncate">{s.latest_episode_at ? `Updated ${timeAgo(s.latest_episode_at)}` : "No episodes yet"}</p>
            </Link>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
